import { generateClient } from 'aws-amplify/api';
import * as qlqueries from '../graphql/queries';
import * as routerMutations from '../graphql/routerMutations';
import * as routerQueries from '../graphql/routerQueries';

const client = generateClient();

const state = {
  inputs: [],
  outputs: [],
  outputStatuses: {},
  statusPollingIntervals: {},
  loading: false,
};

const getters = {
  getOutputStatus: (state) => (outputId) => {
    return state.outputStatuses[outputId] || null;
  },

  getEventOutputStatuses: (state) => (eventId) => {
    return state.outputStatuses[`event_${eventId}`] || {};
  },

  getReadableStatus: (state, getters) => (outputId, outputType = 'OTT') => {
    const status = getters.getOutputStatus(outputId);
    if (!status) return 'Unknown';

    // Map status codes to readable labels
    const statusMappings = {
      'OTT': {
        'Disabled': 'Not Active',
        'Active': 'Streaming',
        'Error': 'Stream Error',
        'Waiting': 'Waiting for Stream'
      },
      'ROUTE': {
        'Undefined': 'Not Set'
      }
    };

    return statusMappings[outputType]?.[status] || status;
  }
};

const mutations = {
  setInputs: (state, payload) => {
    state.inputs = payload.items || [];
  },

  setOutputs: (state, payload) => {
    state.outputs = payload.items || [];
  },

  setLoading: (state, payload) => {
    state.loading = payload;
  },

  setOutputStatus: (state, { outputId, status }) => {
    state.outputStatuses = {
      ...state.outputStatuses,
      [outputId]: status
    };
  },

  setEventOutputStatuses: (state, { eventId, statuses }) => {
    state.outputStatuses = {
      ...state.outputStatuses,
      [`event_${eventId}`]: statuses
    };
    console.log('Event live status: Event output statuses updated:', state.outputStatuses);
  },

  clearOutputStatus: (state, outputId) => {
    const newStatuses = { ...state.outputStatuses };
    delete newStatuses[outputId];
    state.outputStatuses = newStatuses;
  },

  setPollingInterval: (state, { key, intervalId }) => {
    state.statusPollingIntervals[key] = intervalId;
  },

  clearPollingInterval: (state, key) => {
    const intervalId = state.statusPollingIntervals[key];
    if (intervalId) {
      clearInterval(intervalId);
      const newIntervals = { ...state.statusPollingIntervals };
      delete newIntervals[key];
      state.statusPollingIntervals = newIntervals;
    }
  },

  clearAllPollingIntervals: (state) => {
    Object.values(state.statusPollingIntervals).forEach(intervalId => {
      if (intervalId) clearInterval(intervalId);
    });
    state.statusPollingIntervals = {};
  },
};

// Utility function to handle router mutation responses consistently
const handleRouterMutation = (context, operationName, result, successMessage, errorMessage) => {
  const response = result?.data?.[operationName];

  if (!response) {
    console.error(`${operationName} - No response data received`);
    context._vm?.$message?.error(errorMessage || `Failed to execute ${operationName}`);
    return { error: true, message: 'No response data' };
  }

  if (response.success === false) {
    console.error(`${operationName} failed:`, response.message);
    context._vm.$message.error(response.message || errorMessage || `${operationName} failed`);
    return { error: true, message: response.message };
  }

  if (response.success === true) {
    console.log(`${operationName} succeeded:`, response.message);
    if (successMessage) {
      context._vm?.$message?.success(successMessage);
    }
    return response;
  }

  // Handle cases where success flag might be missing
  console.warn(`${operationName} - Success flag missing, treating as success`);
  return response;
};

// Utility function to safely parse dataJson field from RouterCommandResult
const parseDataJson = (dataJson) => {
  if (!dataJson) return null;

  try {
    return JSON.parse(dataJson);
  } catch (error) {
    console.error('Failed to parse dataJson:', error, dataJson);
    return null;
  }
};

const actions = {

  async fetchInputs({ commit }) {
    commit('setLoading', true);
    try {
      const result = await client.graphql({
        query: qlqueries.listInputsRouterAlt,
        variables: {
          limit: 1500,
        },
      });
      const { data } = result;
      commit('setInputs', data.listInputs);
      return data.listInputs;
    } catch (error) {
      console.log('fetchInputs error', error);

      // Check if partial data is available despite the error
      const hasData = !!error.data && !!error.data.listInputs;
      if (hasData) {
        const { data } = error;
        commit('setInputs', data.listInputs);
        this._vm.$message.error(
          { message: 'Oops something went wrong. Can\'t get some of the inputs' },
        );
        return { error: true, message: error.errors };
      }

      this._vm.$message.error(
        { message: 'Oops something went wrong. Can\'t get inputs' },
      );
      return { error: true, message: error.errors };
    } finally {
      commit('setLoading', false);
    }
  },

  async fetchOutputs({ commit }) {
    commit('setLoading', true);
    try {
      const result = await client.graphql({
        query: qlqueries.listOutputsRouterAlt,
        variables: {
          limit: 1500,
        },
      });
      const { data } = result;
      commit('setOutputs', data.listOutputs);
      return data.listOutputs;
    } catch (error) {
      console.log('fetchOutputs error', error);

      // Check if partial data is available despite the error
      const hasData = !!error.data && !!error.data.listOutputs;
      if (hasData) {
        const { data } = error;
        commit('setOutputs', data.listOutputs);
        this._vm.$message.error(
          { message: 'Oops something went wrong. Can\'t get some of the outputs' },
        );
        return { error: true, message: error.errors };
      }

      this._vm.$message.error(
        { message: 'Oops something went wrong. Can\'t get outputs' },
      );
      return { error: true, message: error.errors };
    } finally {
      commit('setLoading', false);
    }
  },

  async routerStartTarget(context, { eventId, targetId }) {
    console.log('routerStartTarget', { eventId, targetId });
    try {
      const result = await client.graphql({
        query: routerMutations.routerStartTarget,
        variables: {
          eventId,
          targetId,
        },
      });

      return handleRouterMutation(
        this,
        'routerStartTarget',
        result,
        'Target started successfully',
        'Failed to start target'
      );
    } catch (error) {
      console.error('routerStartTarget error', error);
      this._vm?.$message?.error('Failed to start router target');
      return { error: true, message: error.message || error.errors };
    }
  },

  async routerStopTarget(context, { eventId, targetId }) {
    try {
      const result = await client.graphql({
        query: routerMutations.routerStopTarget,
        variables: {
          eventId,
          targetId,
        },
      });

      return handleRouterMutation(
        this,
        'routerStopTarget',
        result,
        'Target stopped successfully',
        'Failed to stop target'
      );
    } catch (error) {
      console.error('routerStopTarget error', error);
      context._vm?.$message?.error('Failed to stop router target');
      return { error: true, message: error.message || error.errors };
    }
  },

  async routerSetStreamKey(context, { eventId, targetId, streamKey }) {
    try {
      const result = await client.graphql({
        query: routerMutations.routerSetStreamKey,
        variables: {
          eventId,
          targetId,
          streamKey,
        },
      });

      return handleRouterMutation(
        this,
        'routerSetStreamKey',
        result,
        'Stream key updated successfully',
        'Failed to set stream key'
      );
    } catch (error) {
      console.error('routerSetStreamKey error', error);
      this._vm?.$message?.error('Failed to set stream key');
      return { error: true, message: error.message || error.errors };
    }
  },

  async routerStartEvent(context, { eventId }) {
    try {
      const result = await client.graphql({
        query: routerMutations.routerStartEvent,
        variables: {
          eventId,
        },
      });

      return handleRouterMutation(
        this,
        'routerStartEvent',
        result,
        'Event started successfully',
        'Failed to start event'
      );
    } catch (error) {
      console.error('routerStartEvent error', error);
      this._vm?.$message?.error('Failed to start event');
      return { error: true, message: error.message || error.errors };
    }
  },

  async routerStopEvent(context, { eventId, forceStop = false }) {
    try {
      const result = await client.graphql({
        query: routerMutations.routerStopEvent,
        variables: {
          eventId,
          forceStop,
        },
      });

      return handleRouterMutation(
        this,
        'routerStopEvent',
        result,
        'Event stopped successfully',
        'Failed to stop event'
      );
    } catch (error) {
      console.error('routerStopEvent error', error);
      context._vm?.$message?.error('Failed to stop event');
      return { error: true, message: error.message || error.errors };
    }
  },

  async routerRestartEvent(context, { eventId }) {
    try {
      const result = await client.graphql({
        query: routerMutations.routerRestartEvent,
        variables: {
          eventId,
        },
      });

      return handleRouterMutation(
        this,
        'routerRestartEvent',
        result,
        'Event restarted successfully',
        'Failed to restart event'
      );
    } catch (error) {
      console.error('routerRestartEvent error', error);
      context._vm?.$message?.error('Failed to restart event');
      return { error: true, message: error.message || error.errors };
    }
  },

  async routerStopOutput(context, { eventId }) {
    try {
      const result = await client.graphql({
        query: routerMutations.routerStopOutput,
        variables: {
          eventId,
        },
      });

      return handleRouterMutation(
        this,
        'routerStopOutput',
        result,
        'Event output stopped successfully',
        'Failed to stop event output'
      );
    } catch (error) {
      console.error('routerStopOutput error', error);
      context._vm?.$message?.error('Failed to stop event output');
      return { error: true, message: error.message || error.errors };
    }
  },

  // Manual Routing Mutations
  async routerRoute(context, { inputId, outputId, audioPid, videoPid, streamKey }) {
    try {
      const result = await client.graphql({
        query: routerMutations.routerRoute,
        variables: {
          inputId,
          outputId,
          audioPid,
          videoPid,
          streamKey,
        },
      });

      return handleRouterMutation(
        context,
        'routerRoute',
        result,
        'Route established successfully',
        'Failed to establish route'
      );
    } catch (error) {
      console.error('routerRoute error', error);
      context._vm?.$message?.error('Failed to establish route');
      return { error: true, message: error.message || error.errors };
    }
  },

  // OTT Output Routing
  async routerRouteSetStreamKey(context, { outputId, streamKey }) {
    try {
      const result = await client.graphql({
        query: routerMutations.routerRouteSetStreamKey,
        variables: {
          outputId,
          streamKey,
        },
      });

      return handleRouterMutation(
        context,
        'routerRouteSetStreamKey',
        result,
        'Stream key set successfully',
        'Failed to set stream key'
      );
    } catch (error) {
      console.error('routerRouteSetStreamKey error', error);
      context._vm?.$message?.error('Failed to set stream key');
      return { error: true, message: error.message || error.errors };
    }
  },

  async routerRouteStartTarget(context, { outputId }) {
    try {
      const result = await client.graphql({
        query: routerMutations.routerRouteStartTarget,
        variables: {
          outputId,
        },
      });

      return handleRouterMutation(
        context,
        'routerRouteStartTarget',
        result,
        'Target started successfully',
        'Failed to start target'
      );
    } catch (error) {
      console.error('routerRouteStartTarget error', error);
      context._vm?.$message?.error('Failed to start target');
      return { error: true, message: error.message || error.errors };
    }
  },

  async routerRouteStopTarget(context, { outputId }) {
    try {
      const result = await client.graphql({
        query: routerMutations.routerRouteStopTarget,
        variables: {
          outputId,
        },
      });

      return handleRouterMutation(
        context,
        'routerRouteStopTarget',
        result,
        'Target stopped successfully',
        'Failed to stop target'
      );
    } catch (error) {
      console.error('routerRouteStopTarget error', error);
      context._vm?.$message?.error('Failed to stop target');
      return { error: true, message: error.message || error.errors };
    }
  },

  async routerRouteRestartApplication(context, { outputId }) {
    try {
      const result = await client.graphql({
        query: routerMutations.routerRouteRestartApplication,
        variables: {
          outputId,
        },
      });

      return handleRouterMutation(
        context,
        'routerRouteRestartApplication',
        result,
        'Application restarted successfully',
        'Failed to restart application'
      );
    } catch (error) {
      console.error('routerRouteRestartApplication error', error);
      context._vm?.$message?.error('Failed to restart application');
      return { error: true, message: error.message || error.errors };
    }
  },

  // Stream Router Interface Routing
  async routerRouteStartInterface(context, { outputId }) {
    try {
      const result = await client.graphql({
        query: routerMutations.routerRouteStartInterface,
        variables: {
          outputId,
        },
      });

      return handleRouterMutation(
        context,
        'routerRouteStartInterface',
        result,
        'Interface started successfully',
        'Failed to start interface'
      );
    } catch (error) {
      console.error('routerRouteStartInterface error', error);
      context._vm?.$message?.error('Failed to start interface');
      return { error: true, message: error.message || error.errors };
    }
  },

  async routerRouteStopInterface(context, { outputId }) {
    try {
      const result = await client.graphql({
        query: routerMutations.routerRouteStopInterface,
        variables: {
          outputId,
        },
      });

      return handleRouterMutation(
        context,
        'routerRouteStopInterface',
        result,
        'Interface stopped successfully',
        'Failed to stop interface'
      );
    } catch (error) {
      console.error('routerRouteStopInterface error', error);
      context._vm?.$message?.error('Failed to stop interface');
      return { error: true, message: error.message || error.errors };
    }
  },

  // Input Operations
  async routerRouteRestartEncoder(context, { inputId }) {
    try {
      const result = await client.graphql({
        query: routerMutations.routerRouteRestartEncoder,
        variables: {
          inputId,
        },
      });

      return handleRouterMutation(
        context,
        'routerRouteRestartEncoder',
        result,
        'Encoder restarted successfully',
        'Failed to restart encoder'
      );
    } catch (error) {
      console.error('routerRouteRestartEncoder error', error);
      context._vm?.$message?.error('Failed to restart encoder');
      return { error: true, message: error.message || error.errors };
    }
  },

  async routerStartInput(context, { inputId }) {
    try {
      const result = await client.graphql({
        query: routerMutations.routerStartInput,
        variables: {
          inputId,
        },
      });

      return handleRouterMutation(
        context,
        'routerStartInput',
        result,
        'Input started successfully',
        'Failed to start input'
      );
    } catch (error) {
      console.error('routerStartInput error', error);
      context._vm?.$message?.error('Failed to start input');
      return { error: true, message: error.message || error.errors };
    }
  },

  async routerStopInput(context, { inputId }) {
    try {
      const result = await client.graphql({
        query: routerMutations.routerStopInput,
        variables: {
          inputId,
        },
      });

      return handleRouterMutation(
        context,
        'routerStopInput',
        result,
        'Input stopped successfully',
        'Failed to stop input'
      );
    } catch (error) {
      console.error('routerStopInput error', error);
      context._vm?.$message?.error('Failed to stop input');
      return { error: true, message: error.message || error.errors };
    }
  },

  // Route Disconnection
  async routerRouteOut(context, { inputId, outputId }) {
    try {
      const result = await client.graphql({
        query: routerMutations.routerRouteOut,
        variables: {
          inputId,
          outputId,
        },
      });

      return handleRouterMutation(
        context,
        'routerRouteOut',
        result,
        'Route disconnected successfully',
        'Failed to disconnect route'
      );
    } catch (error) {
      console.error('routerRouteOut error', error);
      context._vm?.$message?.error('Failed to disconnect route');
      return { error: true, message: error.message || error.errors };
    }
  },

  // Output Status Query Actions
  async fetchEventOutputStatuses(context, { eventId, enablePolling = false, pollingInterval = 10000 }) {
    console.log('Event live status: Fetching event output statuses for eventId:', eventId);
    try {
      const result = await client.graphql({
        query: routerMutations.routerEventOutputTargetStatus,
        variables: {
          eventId,
        },
      });

      const response = result?.data?.routerEventOutputTargetStatus;

      if (!response) {
        console.error('fetchEventOutputStatuses - No response data received');
        return { error: true, message: 'No response data' };
      }

      if (response.success === false) {
        console.error('fetchEventOutputStatuses failed:', response.message);
        return { error: true, message: response.message };
      }

      // Parse the dataJson field
      const statusData = parseDataJson(response.dataJson);
      if (statusData) {
        context.commit('setEventOutputStatuses', { eventId, statuses: statusData });
        console.log('Event output statuses updated:', statusData);
      }

      // Set up polling if requested
      if (enablePolling) {
        context.dispatch('startEventStatusPolling', { eventId, pollingInterval });
      }

      return { success: true, data: statusData };
    } catch (error) {
      console.error('fetchEventOutputStatuses error', error);
      return { error: true, message: error.message || error.errors };
    }
  },

  async fetchEventOutputsStatusesBulk(context, { eventIds }) {
    console.log('Event live status: Fetching bulk event output statuses for eventIds:', eventIds);
    try {
      const result = await client.graphql({
        query: routerMutations.routerEventOutputsTargetStatus,
        variables: {
          eventIds,
        },
      });

      const response = result?.data?.routerEventOutputsTargetStatus;

      if (!response) {
        console.error('fetchEventOutputsStatusesBulk - No response data received');
        return { error: true, message: 'No response data' };
      }

      if (response.success === false) {
        console.error('fetchEventOutputsStatusesBulk failed:', response.message);
        return { error: true, message: response.message };
      }

      // Parse the dataJson field
      const statusData = parseDataJson(response.dataJson);
      // Expected format: { eventId: { outputId: { status: "..." } } }
      if (statusData) {
        Object.entries(statusData).forEach(([eventId, statuses]) => {
          context.commit('setEventOutputStatuses', { eventId, statuses });
        });
        console.log('Bulk event output statuses updated:', statusData);
      }

      return { success: true, data: statusData };
    } catch (error) {
      console.error('fetchEventOutputsStatusesBulk error', error);
      return { error: true, message: error.message || error.errors };
    }
  },

  async fetchOutputStatus(context, { outputId, enablePolling = false, pollingInterval = 5000 }) {
    try {
      const result = await client.graphql({
        query: routerMutations.routerOutputTargetStatus,
        variables: {
          outputId,
        },
      });

      const response = result?.data?.routerOutputTargetStatus;

      if (!response) {
        console.error('fetchOutputStatus - No response data received');
        return { error: true, message: 'No response data' };
      }

      if (response.success === false) {
        console.error('fetchOutputStatus failed:', response.message);
        return { error: true, message: response.message };
      }

      // Parse the dataJson field
      const statusData = parseDataJson(response.dataJson);
      if (statusData && statusData.outputId && statusData.status) {
        context.commit('setOutputStatus', {
          outputId: statusData.outputId,
          status: statusData.status
        });
        console.log('Output status updated:', statusData);
      }

      // Set up polling if requested
      if (enablePolling) {
        context.dispatch('startOutputStatusPolling', { outputId, pollingInterval });
      }

      return { success: true, data: statusData };
    } catch (error) {
      console.error('fetchOutputStatus error', error);
      return { error: true, message: error.message || error.errors };
    }
  },

  async fetchOutputsStatuses(context, { outputIds, enablePolling = false, pollingInterval = 5000 }) {
    try {
      const result = await client.graphql({
        query: routerMutations.routerOutputsTargetStatus,
        variables: {
          outputIds,
        },
      });

      const response = result?.data?.routerOutputsTargetStatus;

      if (!response) {
        console.error('fetchOutputsStatuses - No response data received');
        return { error: true, message: 'No response data' };
      }

      if (response.success === false) {
        console.error('fetchOutputsStatuses failed:', response.message);
        return { error: true, message: response.message };
      }

      // Parse the dataJson field - expected format: { outputId: { status: String } }
      const statusData = parseDataJson(response.dataJson);
      if (statusData) {
        // Update each output status
        Object.entries(statusData).forEach(([outputId, statusInfo]) => {
          if (statusInfo && statusInfo.status) {
            context.commit('setOutputStatus', {
              outputId,
              status: statusInfo.status
            });
          }
        });
        console.log('Multiple output statuses updated:', statusData);
      }

      // Set up polling if requested
      if (enablePolling) {
        context.dispatch('startMultipleOutputsStatusPolling', { outputIds, pollingInterval });
      }

      return { success: true, data: statusData };
    } catch (error) {
      console.error('fetchOutputsStatuses error', error);
      return { error: true, message: error.message || error.errors };
    }
  },

  // Polling Actions
  startEventStatusPolling(context, { eventId, pollingInterval = 5000 }) {
    const key = `event_${eventId}`;

    // Clear existing interval if any
    context.commit('clearPollingInterval', key);

    // Start new polling
    const intervalId = setInterval(async () => {
      await context.dispatch('fetchEventOutputStatuses', { eventId, enablePolling: false });
    }, pollingInterval);

    context.commit('setPollingInterval', { key, intervalId });
    console.log(`Started event status polling for event ${eventId} with ${pollingInterval}ms interval`);
  },

  startOutputStatusPolling(context, { outputId, pollingInterval = 5000 }) {
    const key = `output_${outputId}`;

    // Clear existing interval if any
    context.commit('clearPollingInterval', key);

    // Start new polling
    const intervalId = setInterval(async () => {
      await context.dispatch('fetchOutputStatus', { outputId, enablePolling: false });
    }, pollingInterval);

    context.commit('setPollingInterval', { key, intervalId });
    console.log(`Started output status polling for output ${outputId} with ${pollingInterval}ms interval`);
  },

  startMultipleOutputsStatusPolling(context, { outputIds, pollingInterval = 5000 }) {
    const key = `outputs_${outputIds.join('_')}`;

    // Clear existing interval if any
    context.commit('clearPollingInterval', key);

    // Start new polling
    const intervalId = setInterval(async () => {
      await context.dispatch('fetchOutputsStatuses', { outputIds, enablePolling: false });
    }, pollingInterval);

    context.commit('setPollingInterval', { key, intervalId });
    console.log(`Started multiple outputs status polling for ${outputIds.length} outputs with ${pollingInterval}ms interval`);
  },

  stopEventStatusPolling(context, { eventId }) {
    const key = `event_${eventId}`;
    context.commit('clearPollingInterval', key);
    console.log(`Stopped event status polling for event ${eventId}`);
  },

  stopOutputStatusPolling(context, { outputId }) {
    const key = `output_${outputId}`;
    context.commit('clearPollingInterval', key);
    console.log(`Stopped output status polling for output ${outputId}`);
  },

  stopAllStatusPolling(context) {
    context.commit('clearAllPollingIntervals');
    console.log('Stopped all status polling');
  },

  async fetchRouterThumbnails({ commit }, payload) {
    const inputIds = Array.isArray(payload) ? payload : payload?.inputIds;
    const includeErrors = !Array.isArray(payload) && Boolean(payload?.includeErrors);

    if (!inputIds || inputIds.length === 0) {
      return includeErrors ? { thumbnails: {}, errors: [] } : {};
    }

    try {
      const result = await client.graphql({
        query: routerQueries.routerEventInputsThumbnail,
        variables: { inputIds },
      });
      const response = result.data.routerEventInputsThumbnail;

      if (response && response.success && response.dataJson) {
        const parsedData = parseDataJson(response.dataJson);
        console.log('Fetched router thumbnails:', parsedData);

        const thumbnails = parsedData?.thumbnails || {};
        const errors = Array.isArray(parsedData?.errors) ? parsedData.errors : [];

        if (includeErrors) {
          return { thumbnails, errors };
        }

        return thumbnails;
      }

      return includeErrors ? { thumbnails: {}, errors: [] } : {};
    } catch (error) {
      console.error('fetchRouterThumbnails error', error);
      return includeErrors ? { thumbnails: {}, errors: [] } : {};
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
